"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Spinner from "@/app/components/spinner";
import { useRouter } from "@/lib/i18n/routing";
import { validateMagicLinkToken } from "../../actions";
import TokenValidation from "./token-validation";

interface IMagicLinkValidation {
  token: string;
}

// Validates the magic link token from the browser via a server action (POST).
// The token is single-use, so it must not be consumed while the page renders:
// email link scanners fetch the URL before the user clicks it.
export default function MagicLinkValidation({
  token,
}: Readonly<IMagicLinkValidation>) {
  const tErrors = useTranslations("errors");
  const tCommon = useTranslations("common");
  const { replace } = useRouter();

  const started = useRef(false);
  const [pending, setPending] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    // Effects run twice in development; a second call would hit a used token.
    if (started.current) {
      return;
    }
    started.current = true;

    validateMagicLinkToken(token)
      .then(({ redirectTo, error }) => {
        if (redirectTo) {
          replace(redirectTo);
          return;
        }

        setErrorKey(error ?? null);
        setPending(false);
      })
      .catch(() => {
        setErrorKey("unknown");
        setPending(false);
      });
  }, [token, replace]);

  const error = useMemo(
    () =>
      errorKey
        ? {
            title: tErrors(`${errorKey}.title`),
            message: tErrors(`${errorKey}.message`),
          }
        : null,
    [errorKey, tErrors],
  );

  if (pending) {
    return (
      <div className="absolute top-0 right-0 bottom-0 left-0 flex flex-col items-center justify-center bg-white">
        <Spinner />
        <p className="mt-10 text-xl font-bold">{tCommon("loading")}</p>
      </div>
    );
  }

  return <TokenValidation error={error} />;
}
